import { SCROLL_Y_BREAKPOINTS } from "../../constants";

/*---------------------------
| Animation Props
---------------------------*/
const SPEED = 300;
const SPEED_FADE_IN = 300;
const SPEED_FADE_OUT = 0;
const TOP_OFFSET = 75;

const getPageProps = currentPage => {
    const scrollYBreakpoint =
        SCROLL_Y_BREAKPOINTS?.[currentPage]?.row2Animate ||
        SCROLL_Y_BREAKPOINTS.default.row2Animate;

    // isSingleRowStart is true only for SRS (Single Row Start) pages - home,
    // icons landing, photos landing - which have their own in-page hero search
    // bar. Those pages start with only Row 1 visible; Row 2 (its own search
    // bar, logo, and CTAs) is fully hidden (overflow: hidden, zero height)
    // until the hero search bar scrolls out of view, at which point Row 2
    // animates in and Row 1 animates out. Every other page is DRS (Double Row
    // Start): both rows render immediately and Row 2 is never actually
    // hidden, just visually covered by its own full-width search bar until
    // the header goes compact - so nothing there needs animating or hiding.
    let isSingleRowStart = false;
    let overflow = {
        isVisible: true,
    };

    switch (currentPage) {
        case "home":
        case "icons":
        case "photos":
            isSingleRowStart = true;
            overflow = {
                isVisible: false,
            };
            break;
        default:
            break;
    }

    return { scrollYBreakpoint, isSingleRowStart, overflow };
};

// Get Animate IN props
const getAnimateInProps = (isSingleRowStart, isLoggedIn) => {
    return {
        // Vertical :: DRS pages have nothing to animate - Row 2 is already in
        // place - so we skip the top offset there
        top: { value: 0, speed: isSingleRowStart ? SPEED : 0, delay: 0 },

        // Overlay
        left: {
            value: 125,
            speed: SPEED,
            delay: isSingleRowStart ? SPEED * 1 : 0,
        },
        right: {
            value: isLoggedIn ? 148 : 175,
            speed: SPEED,
            delay: isSingleRowStart ? SPEED * 1 : 0,
        },

        // Underlay
        opacity: {
            value: 1,
            speed: SPEED_FADE_IN,
            delay: isSingleRowStart ? SPEED * 2 : SPEED * 1,
        },
    };
};

// Get Animate OUT props
// Also an alias for default props
const getAnimateOutProps = isSingleRowStart => {
    return {
        // Vertical :: DRS pages exclude the top offset; SRS pages push Row 2
        // down offstage until it's revealed
        top: {
            value: isSingleRowStart ? TOP_OFFSET : 0,
            speed: isSingleRowStart ? SPEED : 0,
            delay: SPEED * 1,
        },

        // Overlay
        left: { value: 0, speed: SPEED, delay: 0 },
        right: { value: 0, speed: SPEED, delay: 0 },

        // Underlay
        opacity: { value: 0, speed: SPEED_FADE_OUT, delay: 0 },
    };
};

// When Animating In, we need to wait for animation to complete before we set overflow: visible;
// animateInProps.opacity delay plus speed === total duration of animation in
const getOverflowAnimateInDuration = (pagePropsOverflow, animateInProps) => {
    const { delay, speed } = animateInProps.opacity;
    const animatinInDuration = delay + speed;
    return {
        ...pagePropsOverflow,
        animatinInDuration,
    };
};

const getAnimateProps = (currentPage, isLoggedIn) => {
    const pageProps = getPageProps(currentPage);
    const { isSingleRowStart, scrollYBreakpoint } = pageProps;
    const animateInProps = getAnimateInProps(isSingleRowStart, isLoggedIn);
    const animateOutProps = getAnimateOutProps(isSingleRowStart);
    const overflow = getOverflowAnimateInDuration(pageProps.overflow, animateInProps);
    return {
        styleProps: animateOutProps, // default to animateOutProps
        animateInProps,
        animateOutProps,
        sequence: "out",
        scrollYBreakpoint,
        // Exposed at the top level (not nested under overflow) since it's a
        // general "is this an SRS page" signal, not specific to the overflow-
        // toggling mechanism - e.g. SearchBar.js reads it directly to decide
        // whether Row 2's search bar should be excluded from the tab order.
        isSingleRowStart,
        overflow,
    };
};

export default getAnimateProps;
